import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  LinearProgress,
  Typography
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { LocationStableSelect, BlockColourPicker } from '../../elements';
import { DiseaseStableColour } from '../../models/injection';
import { ApiService } from '../../hooks';
import { LocationStable } from '../../models/locations';
import { HealthSystemDomainMortalityReasonsCommandsCreateMortalityReasonCommand } from '../../api/HealthSystemApi';

interface IProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
}

const CreateMortalityReasonModal: React.FC<any> = ({ opened, onModalAction }: IProps) => {
  const classes = useStyles();
  const apiService = new ApiService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [stableColours, setStableColours] = React.useState([] as DiseaseStableColour[]);

  const [name, setName] = React.useState('');

  const getRandomColour = () => `#${getRandomInt(100000, 900000).toString()}`;

  const getRandomInt = (min: number, max: number) => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
  };

  const closeDialog = (result: boolean) => {
    clearFields();
    onModalAction(result);
  };

  const handleCreate = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    apiService.api.mortalityReasons
      .mortalityReasonsCreateMortalityReason(buildCreateCommand())
      .then(() => {
        notifications.success(t('f-mortality-reasons-add-ok'));
        setIsBusy(false);
        clearFields();
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        setIsBusy(false);
      });
  };

  const buildCreateCommand = (): HealthSystemDomainMortalityReasonsCommandsCreateMortalityReasonCommand => {
    return {
      name,
      stableColours
    };
  };

  const clearFields = () => {
    setName('');
    setStableColours([]);
  };

  const validateForm = (): boolean => {
    if (!name) {
      notifications.warning(t('f-val-mortality-reason-name'));
      return false;
    }

    return true;
  };

  const handleAddStableColourClick = () => {
    setStableColours([...stableColours, { colour: getRandomColour(), stable: LocationStable.Unknown }]);
  };

  const renderStableColoursSelectors = () => {
    return (
      <Grid item container spacing={1} direction="column">
        {stableColours.map((_value, index) => (
          <Grid
            item
            container
            alignItems="center"
            key={`${stableColours[index].stable}-${stableColours[index].colour}`}
          >
            <Grid item md={10}>
              <LocationStableSelect
                value={stableColours[index].stable}
                cssClass={classes.formControl}
                fullWidth
                allowAny
                onLocationStableChange={(value) => handleStableChange(value, index)}
              />
            </Grid>

            <Grid item md={2}>
              <BlockColourPicker
                colour={stableColours[index].colour}
                handleColourChange={(colour: any) => handleStableColourChange(colour, index)}
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleStableChange = (value: number, index: number) => {
    stableColours[index].stable = value;
    setStableColours([...stableColours]);
  };

  const handleStableColourChange = (colour: any, index: number) => {
    stableColours[index].colour = colour.hex;
    setStableColours([...stableColours]);
  };

  return (
    <Dialog
      id="createDiseadeDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      className={classes.dialog}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="createDiseadeDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-mortality-reasons-add-title')}
      </DialogTitle>

      <DialogContent>
        <Grid item container md={12} direction="column">
          <Grid item>
            <TextField
              label={t('f-com-name')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <Typography variant="h6" className={classes.formControl}>
              {t('f-mortality-reasons-colour')}
            </Typography>
          </Grid>

          {renderStableColoursSelectors()}

          <Grid item>
            <Button
              id="addStableColBtn"
              onClick={handleAddStableColourClick}
              size="small"
              startIcon={<Icon>{'add_circle_outline'}</Icon>}
              color="primary"
            >
              {t('f-mortality-reasons-colour-add')}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Button
              onClick={() => closeDialog(false)}
              variant="outlined"
              disabled={isBusy}
              className={classes.formControl}
              startIcon={<Icon>{'close'}</Icon>}
              color="primary"
            >
              {t('f-com-cancel')}
            </Button>

            <Button
              onClick={handleCreate}
              id="createBtn"
              disabled={isBusy}
              className={classes.formControl}
              startIcon={<Icon>{'add_circle_outline'}</Icon>}
              variant="contained"
              color="primary"
            >
              {t('f-com-create')}
            </Button>
          </Grid>

          {isBusy && (
            <Grid item>
              <LinearProgress />
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMortalityReasonModal;
